import React, { useEffect, useState } from 'react';
import Panel from '../Panel';
import LoaderButton from '../LoaderButton';
import LoadingBlinker from '../LoadingBlinker';
import OAuthAPI from '../../lib/OAuthAPI';
import OAuthErrorNotice from './OAuthErrorNotice';
import OAuthSignIn from './OAuthSignIn';

const scopes = [
	'profile:read', 'profile:write', 'clubs:read', 'clubs:write', 'clubs:dangerous', 'squad:read', 'squad:write',
	'tactics:read', 'tactics:write', 'training:read', 'training:write', 'fixtures:read', 'fixtures:write',
	'competitions:read', 'competitions:write', 'market:read', 'market:write', 'social:read', 'social:write',
	'communications:read', 'communications:write', 'unions:read', 'unions:write', 'commerce:read', 'commerce:write', 'offline_access',
];

function blankClient() {
	return { name: '', description: '', homepage_url: '', privacy_policy_url: '', type: 'public', redirect_uris: '', sports: ['rugby'], allowed_scopes: ['profile:read'] };
}

function ClientEditor({ client, onSaved, onDeleted, onSecret }) {
	let [editing, setEditing] = useState(false);
	let [draft, setDraft] = useState({
		name: client.Name,
		description: client.Description,
		homepage_url: client.HomepageUrl,
		privacy_policy_url: client.PrivacyPolicyUrl,
		redirect_uris: (client.RedirectUris || []).join('\n'),
		sports: client.Sports || [],
		allowed_scopes: client.AllowedScopes || [],
		status: client.Status,
	});
	let [loading, setLoading] = useState(null);
	let [error, setError] = useState(null);

	let toggleArray = (key, value) => setDraft(previous => ({ ...previous, [key]: previous[key].includes(value) ? previous[key].filter(item => item !== value) : [...previous[key], value] }));
	let save = async event => {
		event.preventDefault();
		setLoading('save'); setError(null);
		try {
			let result = await OAuthAPI.updateClient(client.Id, { ...draft, redirect_uris: draft.redirect_uris.split(/\n+/).map(value => value.trim()).filter(Boolean) });
			onSaved(result.client); setEditing(false);
		} catch (requestError) { setError(requestError); }
		finally { setLoading(null); }
	};
	let disable = async () => {
		setLoading('disable'); setError(null);
		try { let result = await OAuthAPI.updateClient(client.Id, { status: client.Status === 'disabled' ? 'active' : 'disabled' }); onSaved(result.client); }
		catch (requestError) { setError(requestError); } finally { setLoading(null); }
	};
	let remove = async () => {
		if(!window.confirm(`Delete ${client.Name}? Existing grants will no longer be usable.`)) return;
		setLoading('delete'); setError(null);
		try { await OAuthAPI.deleteClient(client.Id); onDeleted(client.Id); }
		catch (requestError) { setError(requestError); } finally { setLoading(null); }
	};
	let rotate = async () => {
		setLoading('rotate'); setError(null);
		try { onSecret(await OAuthAPI.rotateClientSecret(client.Id)); }
		catch (requestError) { setError(requestError); } finally { setLoading(null); }
	};

	return <div className="oauth-client-card">
		<h3>{client.Name} <span className={`oauth-status ${client.Status}`}>{client.Status}</span></h3>
		<p>{client.Description}</p><p><code>{client.Id}</code></p>
		<p><strong>Sports:</strong> {(client.Sports || []).join(', ')} · <strong>Type:</strong> {client.Type}</p>
		<p><strong>Redirect URIs:</strong></p><ul>{(client.RedirectUris || []).map(uri => <li key={uri}><code>{uri}</code></li>)}</ul>
		<div className="oauth-actions">
			<LoaderButton className="loader-button" isLoading={loading === 'disable'} disabled={Boolean(loading)} onClick={disable}>{client.Status === 'disabled' ? 'Enable' : 'Disable'}</LoaderButton>
			<LoaderButton className="loader-button" isLoading={false} disabled={Boolean(loading)} onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</LoaderButton>
			{client.Type === 'confidential' ? <LoaderButton className="loader-button" isLoading={loading === 'rotate'} disabled={Boolean(loading)} onClick={rotate}>Rotate secret</LoaderButton> : null}
			<LoaderButton className="loader-button oauth-deny" isLoading={loading === 'delete'} disabled={Boolean(loading)} onClick={remove}>Delete</LoaderButton>
		</div>
		{editing ? <ClientFields draft={draft} setDraft={setDraft} toggleArray={toggleArray} includeIdentity={false} onSubmit={save} loading={loading} /> : null}
		{error ? <OAuthErrorNotice error={error} fallback="We could not complete that developer action." /> : null}
	</div>;
}

function ClientFields({ draft, setDraft, toggleArray, includeIdentity, onSubmit, loading }) {
	return <form className="oauth-client-fields" onSubmit={onSubmit}>
		<label>App name<input value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} required /></label>
		<label>Description<textarea value={draft.description} onChange={event => setDraft({ ...draft, description: event.target.value })} minLength="10" required /></label>
		<label>Homepage URL<input type="url" value={draft.homepage_url} onChange={event => setDraft({ ...draft, homepage_url: event.target.value })} required /></label>
		<label>Privacy-policy URL<input type="url" value={draft.privacy_policy_url} onChange={event => setDraft({ ...draft, privacy_policy_url: event.target.value })} required /></label>
		{includeIdentity ? <label>Client type<select value={draft.type} onChange={event => setDraft({ ...draft, type: event.target.value })}><option value="public">Public (PKCE only)</option><option value="confidential">Confidential (secret)</option></select></label> : null}
		<label>Redirect URIs<textarea value={draft.redirect_uris} onChange={event => setDraft({ ...draft, redirect_uris: event.target.value })} placeholder="One exact URI per line" required /></label>
		<fieldset><legend>Sports</legend>{['rugby', 'football'].map(sport => <label key={sport}><input type="checkbox" checked={draft.sports.includes(sport)} onChange={() => toggleArray('sports', sport)} /> {sport}</label>)}</fieldset>
		<fieldset><legend>Allowed scopes</legend>{scopes.map(scope => <label key={scope}><input type="checkbox" checked={draft.allowed_scopes.includes(scope)} onChange={() => toggleArray('allowed_scopes', scope)} /> {scope}</label>)}</fieldset>
		<LoaderButton className="loader-button" isLoading={loading === 'save' || loading === 'create'} disabled={Boolean(loading)} isSubmitButton>{includeIdentity ? 'Create app' : 'Save changes'}</LoaderButton>
	</form>;
}

function OAuthDeveloperApps() {
	let [state, setState] = useState('loading');
	let [clients, setClients] = useState([]);
	let [draft, setDraft] = useState(blankClient());
	let [creating, setCreating] = useState(false);
	let [error, setError] = useState(null);
	let [secret, setSecret] = useState(null);

	let load = () => {
		setState('loading');
		// A previous unauthenticated load can race with the hosted sign-in flow.
		// Clear that stale message before retrying now that the OAuth session exists.
		setError(null);
		OAuthAPI.getClients().then(result => { setClients(result.clients || []); setState('ready'); }).catch(requestError => {
			setState(requestError.response && requestError.response.status === 401 ? 'sign-in' : 'error');
			setError(requestError);
		});
	};
	useEffect(load, []);
	let toggleArray = (key, value) => setDraft(previous => ({ ...previous, [key]: previous[key].includes(value) ? previous[key].filter(item => item !== value) : [...previous[key], value] }));
	let create = async event => {
		event.preventDefault(); setCreating(true); setError(null);
		try {
			let result = await OAuthAPI.createClient({ ...draft, redirect_uris: draft.redirect_uris.split(/\n+/).map(value => value.trim()).filter(Boolean) });
			setClients(previous => [result.client, ...previous]);
			setSecret(result.client_secret ? result : null);
			setDraft(blankClient());
		} catch (requestError) { setError(requestError); } finally { setCreating(false); }
	};

	if(state === 'loading') return <Panel className="oauth-panel"><LoadingBlinker text="Loading developer apps" /></Panel>;
	if(state === 'sign-in') return <Panel className="oauth-panel"><OAuthSignIn onSignedIn={load} /></Panel>;
	if(state === 'error') return <Panel className="oauth-panel"><h2>Developer apps unavailable</h2><OAuthErrorNotice error={error} fallback="We could not load developer apps." /></Panel>;
	return <Panel className="oauth-panel oauth-developer-apps">
		<h2>Developer apps</h2><p>Create up to ten active apps. They are immediately usable after a verified manager creates them.</p>
		{secret ? <div className="oauth-secret"><strong>Copy this client secret now.</strong><code>{secret.client_secret}</code><p>It will not be shown again. The previous secret, if any, works for 24 hours after rotation.</p><button className="link-button" onClick={() => setSecret(null)}>I have stored it safely</button></div> : null}
		<h3>Create an app</h3><ClientFields draft={draft} setDraft={setDraft} toggleArray={toggleArray} includeIdentity={true} onSubmit={create} loading={creating ? 'create' : null} />
		<h3>Your apps</h3>
		{clients.length ? clients.map(client => <ClientEditor key={client.Id} client={client} onSaved={saved => setClients(previous => previous.map(item => item.Id === saved.Id ? saved : item))} onDeleted={id => setClients(previous => previous.filter(item => item.Id !== id))} onSecret={setSecret} />) : <p>You have not created an app yet.</p>}
		{error ? <OAuthErrorNotice error={error} fallback="We could not complete that developer action." /> : null}
	</Panel>;
}

export default OAuthDeveloperApps;
